import React, { useState, useEffect } from 'react';
import { Save, Clock, MapPin, Loader2, RotateCw } from 'lucide-react';
import api from '../api/axiosInstance';

const AttendanceModule = () => {
    const [employees, setEmployees] = useState([]);
    const [projects, setProjects] = useState([]);
    const [attendanceData, setAttendanceData] = useState({});
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [loading, setLoading] = useState(true);

    useEffect(() => { loadData(); }, [selectedDate]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [empRes, projRes, attRes] = await Promise.all([
                api.get('get_employees.php'),
                api.get('get_projects.php'),
                api.get(`get_attendance_by_date.php?date=${selectedDate}`)
            ]);
            setEmployees(empRes.data.employees || []);
            setProjects(projRes.data.projects || []);

            const mapped = {};
            attRes.data.attendance?.forEach(a => {
                mapped[a.employee_id] = {
                    status: a.status,
                    multiplier: a.multiplier || 1,
                    in_time: a.in_time || '',
                    out_time: a.out_time || '',
                    project_id: a.project_id // Capturing the site
                };
            });
            setAttendanceData(mapped);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const updateEntry = (empId, field, value) => {
        setAttendanceData(prev => ({
            ...prev,
            [empId]: {
                ...(prev[empId] || {
                    status: 'Present',
                    multiplier: 1,
                    project_id: employees.find(e => e.id === empId)?.assigned_site_id
                }),
                [field]: value
            }
        }));
    };

    const handleSave = async () => {
        try {
            const payload = employees.map(emp => {
                const data = attendanceData[emp.id] || { status: 'Absent', multiplier: 0 };
                return {
                    employee_id: emp.id,
                    applied_rate: emp.rate,
                    project_id: data.project_id || emp.assigned_site_id, // Fallback to profile site
                    ...data
                };
            });
            await api.post('save_bulk_attendance.php', { date: selectedDate, attendance: payload });
            alert("Attendance & Site Costs Synced!");
        } catch (err) { alert("Error saving logs"); }
    };

    return (
        <div className="space-y-8 p-4 animate-in fade-in duration-500">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-black uppercase italic tracking-tighter text-slate-900">Labor Cost Tracking</h1>
                    <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest">Date: {selectedDate}</p>
                </div>
                <div className="flex items-center gap-3">
                    <button 
                        onClick={loadData} 
                        disabled={loading}
                        title="Reload Attendance Data"
                        className="bg-white hover:bg-slate-50 text-slate-700 px-5 py-3 rounded-xl font-bold flex items-center gap-2 border border-slate-200 shadow-sm transition-all active:scale-95 disabled:opacity-60 text-[10px] uppercase"
                    >
                        <RotateCw size={14} className={`text-indigo-600 ${loading ? 'animate-spin' : ''}`} />
                        <span>{loading ? 'Reloading...' : 'Reload'}</span>
                    </button>
                    <input type="date" className="p-3 bg-white border rounded-xl font-bold text-xs" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
                    <button onClick={handleSave} className="bg-indigo-600 text-white px-8 py-3 rounded-xl font-black text-[10px] uppercase shadow-lg hover:bg-slate-900 transition-all">Save & Attribute Costs</button>
                </div>
            </div>

            <div className="bg-white rounded-[40px] border border-slate-100 shadow-sm overflow-hidden">
                <table className="w-full text-left">
                    <thead className="bg-slate-50 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                        <tr>
                            <th className="p-6">Employee</th>
                            <th className="p-6 text-center">Assigned Site (For Costing)</th>
                            <th className="p-6">Work Log</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {employees.map(emp => {
                            const data = attendanceData[emp.id] || { status: 'Absent', multiplier: 0, project_id: emp.assigned_site_id };
                            return (
                                <tr key={emp.id} className="hover:bg-slate-50/50 transition-colors">
                                    <td className="p-6">
                                        <p className="text-slate-900 font-black uppercase italic text-sm">{emp.name}</p>
                                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-tighter">{emp.pay_type} @ ₹{emp.rate}</p>
                                    </td>
                                    <td className="p-6">
                                        <div className="flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-2xl">
                                            <MapPin size={12} className="text-indigo-500" />
                                            <select
                                                className="bg-transparent border-none outline-none font-black text-[10px] uppercase w-full"
                                                value={data.project_id || ''}
                                                onChange={e => updateEntry(emp.id, 'project_id', e.target.value)}
                                            >
                                                <option value="">Select Site...</option>
                                                {projects.map(p => <option key={p.id} value={p.id}>{p.project_name}</option>)}
                                            </select>
                                        </div>
                                    </td>
                                    <td className="p-6">
                                        {emp.pay_type === 'Daily' && (
                                            <select className="p-2 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase outline-none" value={data.multiplier} onChange={e => updateEntry(emp.id, 'multiplier', e.target.value)}>
                                                {[0, 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4].map(v => <option key={v} value={v}>{v} Shift</option>)}
                                            </select>
                                        )}
                                        {emp.pay_type === 'Monthly' && (
                                            <div className="flex gap-1">
                                                {['Present', 'Absent', 'Half-day'].map(s => (
                                                    <button key={s} onClick={() => updateEntry(emp.id, 'status', s)} className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase transition-all ${data.status === s ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-400'}`}>{s[0]}</button>
                                                ))}
                                            </div>
                                        )}
                                        {emp.pay_type === 'Hourly' && (
                                            <div className="flex gap-2 items-center">
                                                <input type="time" className="p-2 bg-slate-50 rounded-lg text-[10px] font-bold" value={data.in_time} onChange={e => updateEntry(emp.id, 'in_time', e.target.value)} />
                                                <input type="time" className="p-2 bg-slate-50 rounded-lg text-[10px] font-bold" value={data.out_time} onChange={e => updateEntry(emp.id, 'out_time', e.target.value)} />
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AttendanceModule;    